import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { gbp, formatWeight, nextBoxUpgrade, boostLabel, mixDiscountPct, nextOfferStep, bottleOffer, mixboxRetailPrice, mixboxBoxPrice, isTradeMode } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { usePartner } from "@/context/PartnerContext";
import { rememberOrder } from "@/lib/recentOrders";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, ShieldCheck, KeyRound, Link2, ExternalLink, Lock } from "lucide-react";
import { DiscountCodeBox } from "@/components/DiscountCodeBox";
import { useCopy } from "@/lib/copy";
import { useSiteContent } from "@/context/SiteContentContext";
import { encryptForShop, fetchMainShopKey } from "@/lib/pgpClient";

const CURRENCIES = [
  { code: "BTC", name: "Bitcoin", color: "#F7931A" },
  { code: "LTC", name: "Litecoin", color: "#345D9D" },
];

const SEC_LABEL = { pgp: "PGP encrypted", auto: "PGP encrypted (browser)", temp: "temp.pm link" };

export default function Checkout() {
  const { items, clear, updateItem, shipCountry, setShipCountry, discountCode } = useCart();
  const { ref: partnerRef, partner, isPartner } = usePartner();
  const { t, C } = useCopy();
  const { content } = useSiteContent();
  const pgpAvailable = !isPartner || Boolean(partner?.pgp_public_key);
  // temp.pm can be paused from Admin → Content; it stays as a fallback only if there is no PGP key at all
  const tempEnabled = content?.temp_pm_enabled !== false || !pgpAvailable;
  const pgpKeyHref = isPartner ? "/pgp-key" : "/pgp-key.html";
  const [addr, setAddr] = useState({ name: "", address: "", city: "", postcode: "", notes: "" });
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", country: shipCountry || "United Kingdom" });
  const [secMethod, setSecMethod] = useState("auto");
  useEffect(() => { if (!pgpAvailable) setSecMethod("temp"); }, [pgpAvailable]);
  useEffect(() => { if (!tempEnabled && secMethod === "temp") setSecMethod("auto"); }, [tempEnabled, secMethod]);
  const [secureBlob, setSecureBlob] = useState("");
  const [currency, setCurrency] = useState("BTC");
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [placing, setPlacing] = useState(false);
  const [redirecting, setRedirecting] = useState(null);
  const [zones, setZones] = useState([]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, email: f.email || user.email || "" }));
  }, [user]);

  useEffect(() => {
    api.get("/shipping/zones").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  const sortedZones = useMemo(
    () => zones.map((z) => ({ ...z, countries: [...(z.countries || [])].sort() })),
    [zones]
  );

  const isWholesale = items.some((i) => i.is_wholesale);

  const buildItems = () => items.map((it) => {
    if (it.type === "single") return { type: "single", flavor_id: it.flavor_id, flavor_name: it.flavor_name, base: it.base, size_id: it.size_id, quantity: it.quantity };
    if (it.type === "mixbox") return { type: "mixbox", size_id: it.size_id, selections: it.selections, quantity: it.quantity, mixpack_discount: it.mixpack_discount || 0 };
    if (it.type === "economy") return { type: "economy", size_id: it.size_id, selections: it.selections.map((s) => ({ flavor_id: s.flavor_id, boost: s.boost, quantity: s.quantity })), quantity: it.quantity };
    if (it.type === "guest") return { type: "guest", flavor_id: it.flavor_id, quantity: it.quantity, options: (it.options || []).map((o) => o.label) };
    if (it.type === "offer") return { type: "offer", flavor_id: it.flavor_id, base: it.base, size_id: it.size_id, quantity: it.quantity };
    return { type: "multipack", multipack_id: it.multipack_id, selections: it.selections, quantity: it.quantity };
  });

  const buildPayload = (blobOverride) => ({
    items: buildItems(),
    customer_name: "Web order",
    email: (form.email && form.email.trim()) ? form.email.trim().toLowerCase() : `web-${Math.random().toString(36).slice(2, 10)}@checkout.dreamlabs.uk`,
    address: (blobOverride ?? secureBlob).trim() || "N/A",
    city: SEC_LABEL[secMethod] || "secure",
    postcode: "—",
    country: form.country,
    currency,
    is_wholesale: isWholesale,
    discount_code: discountCode || null,
    ref: partnerRef || null,
  });

  useEffect(() => {
    if (items.length === 0) return;
    const payload = { items: buildItems(), currency, is_wholesale: isWholesale, country: form.country, customer_name: "x", email: "quote@dreamlabs.uk", address: "x", city: "x", postcode: "x", discount_code: discountCode || null, ref: partnerRef || null };
    api.post("/checkout/quote", payload)
      .then((r) => { setQuote(r.data); setQuoteError(""); })
      .catch((e) => { setQuote(null); setQuoteError(apiError(e)); });
    // eslint-disable-next-line
  }, [items, currency, form.country, discountCode]);

  if (redirecting) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-28 text-center" data-testid="checkout-redirecting">
        <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Order {redirecting.order_number}</div>
        <h1 className="mt-3 font-heading text-3xl font-bold">{t("co_redirect_title", { coin: CURRENCIES.find((c) => c.code === redirecting.currency)?.name || redirecting.currency })}</h1>
        <p className="mt-3 text-sm text-muted-foreground"><C k="co_redirect_body" vars={{ coin: CURRENCIES.find((c) => c.code === redirecting.currency)?.name || redirecting.currency }} /></p>
        <a href={redirecting.checkout_url} data-testid="checkout-redirect-link" className="mono mt-6 inline-block text-xs text-primary underline">Not redirected? Tap here</a>
      </div>
    );
  }

  if (items.length === 0) {
    return <div className="mx-auto max-w-[1400px] px-6 py-28 text-center"><h1 className="font-heading text-3xl font-bold">Nothing to checkout</h1></div>;
  }

  const placeOrder = async () => {
    let blob = secureBlob.trim();
    if (secMethod === "auto") {
      const missing = ["name", "address", "city", "postcode"].filter((k) => !addr[k].trim());
      if (missing.length) { toast.error("Please fill in your name, address, town/city and postcode"); return; }
    } else {
      if (!blob) { toast.error("Please add your delivery details securely below"); return; }
      if (secMethod === "pgp" && !/BEGIN PGP MESSAGE/.test(blob)) { toast.error("Paste the full PGP block — it starts with -----BEGIN PGP MESSAGE-----"); return; }
      if (secMethod === "temp" && !/^https?:\/\//i.test(blob)) { toast.error("Paste the full temp.pm link (it starts with https://)"); return; }
    }
    if (form.email && form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) { toast.error("Enter a valid email or leave it blank"); return; }
    setPlacing(true);
    try {
      if (secMethod === "auto") {
        try {
          const armored = isPartner ? partner.pgp_public_key : await fetchMainShopKey();
          blob = await encryptForShop(armored, addr);
        } catch (e) {
          toast.error("Couldn't encrypt in your browser — try the paste-your-own PGP option or refresh the page");
          setPlacing(false); return;
        }
        setAddr({ name: "", address: "", city: "", postcode: "", notes: "" });
      }
      const { data } = await api.post("/orders", buildPayload(blob));
      rememberOrder(data);
      const statusPath = `/order/${data.id}${data.access_token ? `?token=${encodeURIComponent(data.access_token)}` : ""}`;
      if (data.checkout_url) {
        setRedirecting(data);
        clear();
        window.location.assign(data.checkout_url);
        return;
      }
      clear();
      nav(statusPath);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPlacing(false);
    }
  };

  const upgrades = items.map((it) => ({ it, up: nextBoxUpgrade(it) })).filter((x) => x.up);
  const mixpackSavings = Math.round(items.reduce((a, it) => {
    const d = it.mixpack_discount || 0;
    const lt = (it.box_price ?? it.unit_price * it.bottle_count) * it.quantity;
    return it.type === "mixbox" && d > 0 ? a + lt * (d / (1 - d)) : a;
  }, 0) * 100) / 100;
  const retailSavings = isTradeMode() ? Math.round(items.reduce((a, it) => {
    if (it.type !== "mixbox") return a;
    const r = mixboxRetailPrice(it);
    return r == null ? a : a + Math.max(0, (r - mixboxBoxPrice(it)) * it.quantity);
  }, 0) * 100) / 100 : 0;
  const applyUpgrade = (it, up) => {
    updateItem(it.key, { selections: up.selections, box_price: up.box_price });
    toast.success(`Box upgraded to ${boostLabel(up.boost)}`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-14">
      <h1 className="font-heading text-5xl font-black tracking-tighter">Checkout</h1>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {isWholesale && <span className="mono inline-block border border-foreground px-3 py-1 text-[10px] uppercase tracking-[0.2em]">Wholesale pricing applied</span>}
        {!user && (
          <span data-testid="guest-checkout-badge" className="mono inline-flex items-center gap-2 border border-border px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Guest checkout · no account needed
          </span>
        )}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Delivery + payment */}
        <div className="space-y-8 lg:col-span-2">
          <section className="border border-border p-6" data-testid="secure-delivery">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("co_private_label")}</div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground"><C k={isPartner ? "co_private_intro_partner" : "co_private_intro"} /></p>

            <details className="mt-4 border border-border bg-muted/30" data-testid="secure-help">
              <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold hover:bg-muted/50">
                <span className="mr-1">{t("co_help_summary")}</span>
                <span className="font-normal text-muted-foreground">{t("co_help_summary_sub")}</span>
              </summary>
              <div className="space-y-3 border-t border-border px-4 py-3 text-sm text-muted-foreground">
                <p><C k="co_help_p1" /></p>
                {pgpAvailable && <p><C k="co_help_auto" /></p>}
                {tempEnabled && <p><C k="co_help_temp" /></p>}
                {pgpAvailable && <p><C k="co_help_pgp" /></p>}
                <p className="text-foreground"><C k="co_help_footer" /></p>
              </div>
            </details>

            <div className="mt-5">
              <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Country</Label>
              <select data-testid="checkout-country" value={form.country} onChange={(e) => { setForm({ ...form, country: e.target.value }); setShipCountry(e.target.value); }} className="mt-1.5 block w-full rounded-none border border-border bg-background p-2.5 text-sm">
                {sortedZones.map((z) => (
                  <optgroup key={z.name} label={`${z.name}`}>
                    {z.countries.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className={`mt-6 grid gap-3 ${{ 3: "sm:grid-cols-3", 2: "grid-cols-2", 1: "grid-cols-1" }[(pgpAvailable ? 2 : 0) + (tempEnabled ? 1 : 0)] || "grid-cols-1"}`}>
              {pgpAvailable && <button type="button" data-testid="sec-method-auto" onClick={() => setSecMethod("auto")} aria-pressed={secMethod === "auto"}
                className={`flex items-start gap-2 border p-3 text-left transition-all ${secMethod === "auto" ? "border-primary bg-primary/10" : "border-border hover:border-foreground"}`}>
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span><span className="block text-sm font-semibold leading-tight">{t("co_auto_tile_title")}</span><span className="block text-[11px] text-muted-foreground">{t("co_auto_tile_sub")}</span></span>
              </button>}
              {tempEnabled && <button type="button" data-testid="sec-method-temp" onClick={() => setSecMethod("temp")} aria-pressed={secMethod === "temp"}
                className={`flex items-start gap-2 border p-3 text-left transition-all ${secMethod === "temp" ? "border-primary bg-primary/10" : "border-border hover:border-foreground"}`}>
                <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span><span className="block text-sm font-semibold leading-tight">{t("co_temp_tile_title")}</span><span className="block text-[11px] text-muted-foreground">{t("co_temp_tile_sub")}</span></span>
              </button>}
              {pgpAvailable && <button type="button" data-testid="sec-method-pgp" onClick={() => setSecMethod("pgp")} aria-pressed={secMethod === "pgp"}
                className={`flex items-start gap-2 border p-3 text-left transition-all ${secMethod === "pgp" ? "border-primary bg-primary/10" : "border-border hover:border-foreground"}`}>
                <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span><span className="block text-sm font-semibold leading-tight">{t("co_pgp_tile_title")}</span><span className="block text-[11px] text-muted-foreground">{t("co_pgp_tile_sub")}</span></span>
              </button>}
            </div>

            {secMethod === "auto" ? (
              <div className="mt-5" data-testid="sec-auto-panel">
                <p className="text-sm text-muted-foreground"><C k="co_auto_intro" /></p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("co_auto_name")}</Label>
                    <Input data-testid="checkout-auto-name" autoComplete="off" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} className="mt-1.5 rounded-none" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("co_auto_address")}</Label>
                    <Input data-testid="checkout-auto-address" autoComplete="off" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} className="mt-1.5 rounded-none" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("co_auto_city")}</Label>
                    <Input data-testid="checkout-auto-city" autoComplete="off" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="mt-1.5 rounded-none" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("co_auto_postcode")}</Label>
                    <Input data-testid="checkout-auto-postcode" autoComplete="off" value={addr.postcode} onChange={(e) => setAddr({ ...addr, postcode: e.target.value })} className="mt-1.5 rounded-none" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("co_auto_notes")}</Label>
                    <Input data-testid="checkout-auto-notes" autoComplete="off" value={addr.notes} onChange={(e) => setAddr({ ...addr, notes: e.target.value })} placeholder={t("co_auto_notes_placeholder")} className="mt-1.5 rounded-none" />
                  </div>
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-muted-foreground"><Lock className="mt-0.5 h-3 w-3 shrink-0 text-primary" /><span><C k="co_auto_note" /></span></p>
              </div>
            ) : secMethod === "pgp" ? (
              <div className="mt-5" data-testid="sec-pgp-panel">
                <ol className="space-y-1.5 text-sm text-muted-foreground">
                  <li><C k="co_pgp_step1" /></li>
                  <li><C k="co_pgp_step2" /></li>
                  <li><C k="co_pgp_step3" /></li>
                </ol>
                <a href={pgpKeyHref} target="_blank" rel="noopener noreferrer" data-testid="pgp-key-link"
                  className="mt-3 inline-flex items-center gap-2 border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">
                  <KeyRound className="h-3.5 w-3.5" /> {t("co_pgp_button")} <ExternalLink className="h-3 w-3" />
                </a>
                <textarea data-testid="checkout-pgp-input" value={secureBlob} onChange={(e) => setSecureBlob(e.target.value)}
                  rows={6} placeholder="-----BEGIN PGP MESSAGE-----&#10;...&#10;-----END PGP MESSAGE-----"
                  className="mono mt-3 w-full rounded-none border border-border bg-background p-3 text-xs" />
              </div>
            ) : (
              <div className="mt-5" data-testid="sec-temp-panel">
                <ol className="space-y-1.5 text-sm text-muted-foreground">
                  <li><C k="co_temp_step1" /></li>
                  <li><C k="co_temp_step2" /></li>
                  <li><C k="co_temp_step3" /></li>
                </ol>
                <Input data-testid="checkout-temp-input" value={secureBlob} onChange={(e) => setSecureBlob(e.target.value)}
                  placeholder="https://temp.pm/..." className="mt-3 rounded-none" />
              </div>
            )}

            <div className="mt-6">
              <Label className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("co_email_label")} <span className="normal-case tracking-normal">{t("co_email_hint")}</span></Label>
              <Input data-testid="checkout-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("co_email_placeholder")} className="mt-1.5 rounded-none" />
              <p className="mt-1.5 text-[11px] text-muted-foreground"><C k="co_email_note" /></p>
            </div>
          </section>

          <section className="border border-border p-6">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("co_payment_label")}</div>
            <p className="mt-2 text-sm text-muted-foreground"><C k="co_payment_body" /></p>
          </section>
        </div>

        {/* Order summary */}
        <div className="h-fit border border-border p-6">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Order Summary</div>
          <div className="mt-4 space-y-2 text-sm">
            {items.map((it) => (
              <div key={it.key} className="flex justify-between gap-2">
                <span className="text-muted-foreground">{it.type === "mixbox" ? `${it.bottle_count}× ${it.size_label} box` : it.type === "economy" ? `${it.bottle_count}× ${it.size_label} economy box${(it.selections || []).some((s) => s.boost && s.boost !== "standard") ? ` · ${(it.selections || []).filter((s) => s.boost && s.boost !== "standard").map((s) => `${s.quantity}× ${s.boost}`).join(", ")}` : ""}` : it.type === "guest" ? `${it.quantity}× ${it.flavor_name}${it.size_label ? ` ${it.size_label}` : ""}${(it.options && it.options.length) ? ` (${it.options.map((o) => o.label).join(", ")})` : ""}` : `${it.quantity}× ${it.type === "single" || it.type === "offer" ? `${it.flavor_name} ${it.size_label}${it.type === "offer" ? " (offer)" : ""}` : it.name}`}{it.type === "single" && it.boost && it.boost !== "standard" ? ` · ${it.boost_label}` : ""}{it.type === "mixbox" && (it.selections || []).some((s) => s.boost && s.boost !== "standard") ? ` · ${(it.selections || []).filter((s) => s.boost && s.boost !== "standard").map((s) => `${s.quantity}× ${s.boost}`).join(", ")}` : ""}</span>
                <span className="mono">{gbp(((it.type === "mixbox" || it.type === "economy") ? (it.box_price ?? it.unit_price * it.bottle_count) : it.unit_price) * it.quantity)}</span>
              </div>
            ))}
          </div>
          {upgrades.map(({ it, up }) => (
            <div key={it.key} className="mt-3 flex flex-wrap items-center justify-between gap-3 border border-primary/40 bg-primary/10 px-3 py-2" data-testid={`checkout-upsell-${it.key}`}>
              <div className="flex items-center gap-2 text-xs">
                <Zap className="h-4 w-4 shrink-0 text-primary" />
                <span>Make every bottle in your {it.size_label} box <span className="font-bold text-primary">{boostLabel(up.boost)}</span> — <span className="font-bold">+{gbp(up.per_bottle)} per bottle</span> · +{gbp(up.delta)} for the box</span>
              </div>
              <button data-testid={`checkout-upsell-btn-${it.key}`} onClick={() => applyUpgrade(it, up)}
                className="shrink-0 border border-primary bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                Upgrade to {up.short}
              </button>
            </div>
          ))}
          <div className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="mono" data-testid="checkout-subtotal">{quote ? gbp(quote.subtotal + (quote.discount?.guest_bundle_saving || 0) + mixpackSavings) : "—"}</span></div>
            {(quote?.discount?.guest_bundle_saving || 0) > 0 && (
              <div className="flex justify-between text-primary" data-testid="checkout-bundle-savings"><span>Guest bundle saving ({quote.discount.guest_bundle_pct}%)</span><span className="mono font-semibold">−{gbp(quote.discount.guest_bundle_saving)}</span></div>
            )}
            {mixpackSavings > 0 && (
              <div className="flex justify-between text-primary" data-testid="checkout-mixpack-savings"><span>Mixed-size saving ({mixDiscountPct()}%)</span><span className="mono font-semibold">−{gbp(mixpackSavings)}</span></div>
            )}
            {retailSavings > 0 && (
              <div className="flex justify-between text-green-600" data-testid="checkout-retail-savings"><span>Your trade price vs standard retail</span><span className="mono font-semibold">−{gbp(retailSavings)}</span></div>
            )}
            {(quote?.discount?.volume_saving || 0) > 0 && (
              <div className="flex justify-between text-primary" data-testid="checkout-volume-savings"><span>Volume discount ({quote.discount.volume_pct}% off)</span><span className="mono font-semibold">−{gbp(quote.discount.volume_saving)}</span></div>
            )}
            {(quote?.discount?.code_saving || 0) > 0 && (
              <div className="flex justify-between text-primary" data-testid="checkout-code-savings"><span>Code {quote.discount.code?.code}</span><span className="mono font-semibold">−{gbp(quote.discount.code_saving)}</span></div>
            )}
            <DiscountCodeBox quote={quote} testPrefix="checkout" />
            <div className="flex justify-between"><span>Weight</span><span className="mono">{quote ? formatWeight(quote.total_weight_g) : "—"}</span></div>
            <div className="flex justify-between">
              <span>Shipping {quote?.shipping_meta?.zone_name ? <span className="text-muted-foreground">· {quote.shipping_meta.zone_name}</span> : ""}</span>
              <span className="mono" data-testid="checkout-shipping">{!quote ? "—" : quote.shipping === 0 ? "FREE" : gbp(quote.shipping)}</span>
            </div>
            {quote?.shipping_meta?.free_shipping_applied && (
              <div className="mono text-[11px] uppercase tracking-[0.1em] text-green-600" data-testid="free-shipping-note">✓ Free shipping applied</div>
            )}
            {!quote?.shipping_meta?.free_shipping_applied && quote?.shipping_meta?.free_shipping_threshold && (() => {
              const gap = quote.shipping_meta.free_shipping_threshold - (quote.subtotal_after_discount ?? quote.subtotal);
              const step = nextOfferStep(items);
              return step > 0 && step >= gap ? (
                <div className="text-[11px] font-semibold text-primary" data-testid="checkout-offer-free-shipping-nudge">Add one {bottleOffer().size_ml}ml offer bottle ({gbp(step)}) and unlock free {quote.shipping_meta.zone_name} shipping — saving {gbp(quote.shipping)}</div>
              ) : (
                <div className="text-[11px] text-muted-foreground">Add {gbp(gap)} more for free {quote.shipping_meta.zone_name} shipping</div>
              );
            })()}
          </div>
          <div className="mt-4 flex justify-between border-t border-border pt-4">
            <span className="font-heading text-lg font-bold">Total</span>
            <span className="font-heading text-lg font-black mono" data-testid="checkout-total">{quote ? gbp(quote.total) : "—"}</span>
          </div>
          <div className="mt-6 border-t border-border pt-4">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Pay with</div>
            <div className="mt-3 grid grid-cols-2 gap-3" data-testid="currency-selector">
              {CURRENCIES.map((c) => (
                <button key={c.code} type="button" data-testid={`currency-${c.code}`} onClick={() => setCurrency(c.code)} aria-pressed={currency === c.code}
                  className={`flex items-center gap-3 border p-3 text-left transition-all ${currency === c.code ? "border-primary bg-primary/10 -translate-y-0.5" : "border-border hover:border-foreground"}`}>
                  <span className="h-7 w-7 shrink-0 rounded-full" style={{ background: c.color }} />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-tight">{c.name}</span>
                    <span className="mono block text-[10px] text-muted-foreground">{c.code}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
          <button data-testid="place-order-btn" disabled={placing || !!quoteError || !quote} onClick={placeOrder}
            className="mt-4 w-full bg-foreground py-4 text-sm font-medium text-background transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-40">
            {placing ? "Creating order…" : `Pay with ${CURRENCIES.find((c) => c.code === currency)?.name || currency} →`}
          </button>
          {quoteError && (
            <div data-testid="quote-error" className="mt-3 border border-destructive bg-destructive/10 px-4 py-3 text-sm text-destructive">{quoteError}</div>
          )}
        </div>
      </div>
    </div>
  );
}
